import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  Fragment,
  DependencyList,
  Dispatch,
  SetStateAction,
  ReactNode,
} from 'react';
import { useLoaderData, useMatch, useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  AutocompleteItem,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Checkbox,
  Chip,
  Divider,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Radio,
  RadioGroup,
  ScrollShadow,
  Select,
  // For some reason this chokes Parcel
  // SelectItem,
  Spinner,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
  User,
  useDisclosure,
} from '@nextui-org/react';
// This doesn't choke Parcel?
import { SelectItem } from './other-objekts';
import { ChevronDownIcon, ChevronUpIcon } from '@nextui-org/shared-icons';
import { useAsyncList } from '@react-stately/data';
import { useDebounce } from 'use-debounce';

import { motion, AnimatePresence } from 'framer-motion';
import { TRANSITION_VARIANTS } from '@nextui-org/framer-utils';
import { useWindowVirtualizer } from '@tanstack/react-virtual';

import { Search } from 'js-search';
import {
  Contract,
  AlchemyProvider,
  Wallet,
  isError as isEthersError,
} from 'ethers';

import { Objekt, Paginated, Profile, cosmoClient } from '../cosmo';
import { useCurrentAccount } from '../credentials';
import { getWalletMnemonic } from '../ramper';
import { polarisClient } from '../polaris';

import { ObjektFilters, ObjektList } from '../components/objekts';
import { Link } from './root';
import { ErrorIcon, CheckIcon, SendIcon, HashIcon } from '../icons';
import { useSearchParamState, useSearchParamStates } from '../hooks';

export type Member = {
  id: number;
  name: string;
  artist: string;
  units: string[];
  alias: string;
  profileImageUrl: string;
  mainObjektImageUrl: string;
  order: number;
};

export type Artist = {
  name: string;
  title: string;
  fandomName: string;
  logoImageUrl: string;
  contracts: {
    Como: string;
    Objekt: string;
    ObjektMinter: string;
    Governor: string;
    CommunityPool: string;
    ComoMinter: string;
  };
  members: Member[];
};

export async function loader() {
  let [{ artist: tripleS }, { artist: artms }] = await Promise.all([
    cosmoClient.get<{ artist: Artist }>('/artist/v1/tripleS'),
    cosmoClient.get<{ artist: Artist }>('/artist/v1/artms'),
  ]);
  let response = await fetch('https://cosmo.goranmoomin.dev/objekts.json');
  let json = await response.json();
  return {
    objekts: (json as any[])
      .filter(item => 'objekt' in item)
      .map(item => item.objekt as Objekt),
    artists: [tripleS, artms],
  };
}

export function usePaginatedObjekts(
  getPage: (
    startAfter?: number,
  ) => Promise<Paginated<{ total: number; objekts: Objekt[] }>>,
  deps: DependencyList,
): Objekt[] {
  let [objekts, setObjekts] = useState<Objekt[]>([]);
  useEffect(() => {
    setObjekts([]);
    let isCancelled = false;
    (async () => {
      let page = await getPage();
      if (!isCancelled) {
        setObjekts(objekts => [...objekts, ...page.objekts]);
      }
      if (page.hasNext) {
        let nextStartAfter = parseInt(page.nextStartAfter);
        let promises: Promise<void>[] = [];
        for (
          let startAfter = nextStartAfter;
          startAfter < page.total;
          startAfter += nextStartAfter
        ) {
          promises.push(
            getPage(startAfter).then(page => {
              if (!isCancelled) {
                setObjekts(objekts => [...objekts, ...page.objekts]);
              }
            }),
          );
        }
        await Promise.all(promises);
      }
    })();
    return () => void (isCancelled = true);
  }, deps);
  return objekts;
}

function CosmoNicknameAutocomplete({
  nickname,
  setNickname,
  isInvalid = false,
  errorMessage,
}: {
  nickname: string;
  setNickname: Dispatch<SetStateAction<string>>;
  isInvalid?: boolean;
  errorMessage?: ReactNode;
}) {
  let list = useAsyncList<Profile>({
    async load({ filterText }) {
      if (!filterText || filterText.length < 4) {
        return { items: [] };
      }
      let { results: items } = await cosmoClient.searchUsers(filterText);
      return {
        items,
      };
    },
  });
  let [listFilterText] = useDebounce(nickname, 100);
  useEffect(() => list.setFilterText(listFilterText), [listFilterText]);
  return (
    <Autocomplete
      isClearable
      label='COSMO ID'
      placeholder='Recipient COSMO ID'
      allowsCustomValue
      isLoading={list.isLoading}
      items={list.items}
      inputValue={nickname}
      onInputChange={setNickname}
      inputProps={{
        classNames: {
          input: 'text-base placeholder:text-small',
        },
      }}
      popoverProps={{ placement: 'top' }}
      isInvalid={isInvalid}
      errorMessage={errorMessage}
    >
      {list.items.map(item => (
        <AutocompleteItem key={item.nickname} textValue={item.nickname}>
          <User
            name={item.nickname}
            description={item.address}
            avatarProps={{
              src: 'https://static.cosmo.fans/uploads/images/img_profile_gallag@3x.png',
            }}
          />
        </AutocompleteItem>
      ))}
    </Autocomplete>
  );
}

export function ObjektModal({
  objekt,
  onClose,
  ownedObjekts,
  selectedObjekts,
  setSelectedObjekts,
}: {
  objekt: Objekt;
  onClose: () => void;
  ownedObjekts: Objekt[];
  selectedObjekts: Objekt[];
  setSelectedObjekts: Dispatch<SetStateAction<Objekt[]>>;
}) {
  let [objektNo, setObjektNo] = useState('');
  let [objektCount, setObjektCount] = useState<number>(0);

  let [transfers, setTransfers] = useState<
    | {
        from: string;
        to: string;
        blockNumber: number;
      }[]
    | null
  >(null);
  let [nicknames, setNicknames] = useState<{
    [address: string]: string;
  }>({});
  let [timestamps, setTimestamps] = useState<{
    [blockNumber: number]: number;
  }>({});

  function onToggleObjektSelection(objekt: Objekt, isSelected: boolean) {
    setSelectedObjekts(currentSelectedObjekts => {
      if (isSelected) {
        return [...currentSelectedObjekts, objekt];
      } else {
        return currentSelectedObjekts.filter(
          selectedObjekt => selectedObjekt !== objekt,
        );
      }
    });
  }

  useEffect(() => {
    let isCancelled = false;
    (async () => {
      let objektCount = await polarisClient.getObjektCount(objekt.collectionId);
      if (!isCancelled) {
        setObjektCount(objektCount);
      }
    })();
    return () => void (isCancelled = true);
  }, [objekt]);

  let [isLoading, setIsLoading] = useState(false);
  let [isFlipped, setIsFlipped] = useState(false);

  return (
    <>
      <ModalHeader>{objekt.collectionId}</ModalHeader>
      <ModalBody>
        <div className='flex flex-col sm:flex-row items-stretch gap-6'>
          <div className='w-[12rem] self-center sm:min-w-[16rem]'>
            <div
              className='relative w-full aspect-w-[1083] aspect-h-[1673] transition-transform'
              style={{
                transformStyle: 'preserve-3d',
                transform: isFlipped ? 'rotateY(180deg)' : '',
              }}
              onClick={() => setIsFlipped(!isFlipped)}
            >
              <div
                className='absolute inset-0'
                style={{ backfaceVisibility: 'hidden' }}
              >
                <img src={objekt.frontImage} alt='Objekt Image' />
              </div>
              <div
                className='absolute inset-0'
                style={{
                  backfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)',
                }}
              >
                <img src={objekt.backImage} alt='Objekt Back Image' />
              </div>
            </div>
          </div>
          <div className='flex-1'>
            <div>
              <span className='font-bold'>Artists:</span>{' '}
              {objekt.artists?.join(', ')}
            </div>
            <div>
              <span className='font-bold'>Season:</span> {objekt.season}
            </div>
            <div>
              <span className='font-bold'>Class:</span> {objekt.class}
            </div>
            <div>
              <span className='font-bold'>Member:</span> {objekt.member}
            </div>
            <div>
              <span className='font-bold'>Total Objekt Count:</span>{' '}
              {objektCount}
            </div>
            <Tabs aria-label='Tabs' className='mt-4'>
              <Tab key='owned' title='Owned'>
                {/* TODO: handle scrolling */}
                {ownedObjekts.length ? (
                  <div className='flex flex-col'>
                    {ownedObjekts.map(ownedObjekt => (
                      <Checkbox
                        key={ownedObjekt.tokenId}
                        isSelected={selectedObjekts.includes(ownedObjekt)}
                        isDisabled={!ownedObjekt.transferable}
                        onValueChange={isSelected =>
                          onToggleObjektSelection(ownedObjekt, isSelected)
                        }
                      >
                        {ownedObjekt.collectionId} #{ownedObjekt.objektNo}
                      </Checkbox>
                    ))}
                  </div>
                ) : (
                  <div className='flex h-40 justify-center items-center text-foreground-400'>
                    No objekts owned.
                  </div>
                )}
              </Tab>
              <Tab key='details' title='Details'>
                <form
                  className='flex flex-row gap-3'
                  onSubmit={async e => {
                    e.preventDefault();
                    if (!objektNo) {
                      return;
                    }
                    setIsLoading(true);
                    try {
                      let transfers = await polarisClient.getObjektTransfers(
                        objekt.collectionId,
                        parseInt(objektNo),
                      );
                      let addresses = new Set<string>();
                      let blockNumbers = new Set<number>();
                      for (let transfer of transfers) {
                        if (
                          transfer.from !==
                          '0x0000000000000000000000000000000000000000'
                        ) {
                          addresses.add(transfer.from);
                        }
                        addresses.add(transfer.to);
                        blockNumbers.add(transfer.blockNumber);
                      }
                      let [nicknames, timestamps] = await Promise.all([
                        getNicknames(Array.from(addresses)),
                        getTimestamps(Array.from(blockNumbers)),
                      ]);
                      setTransfers(transfers);
                      setNicknames(nicknames);
                      setTimestamps(timestamps);
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                >
                  <Input
                    type='number'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    placeholder='Objekt Number'
                    startContent={
                      <HashIcon className='text-foreground-500 w-6 h-6' />
                    }
                    value={objektNo}
                    onValueChange={setObjektNo}
                    classNames={{ input: 'text-base placeholder:text-small' }}
                    disabled={isLoading}
                  />
                  <Button
                    type='submit'
                    isIconOnly
                    color='primary'
                    variant='flat'
                    isLoading={isLoading}
                  >
                    <SendIcon className='w-6 h-6' />
                  </Button>
                </form>
                {/* TODO: handle scrolling */}
                <Table
                  className='mt-4'
                  removeWrapper
                  isStriped
                  layout='fixed'
                  classNames={{ td: 'group-data-[odd=true]:before:-z-10' }}
                >
                  <TableHeader>
                    <TableColumn className='w-40'>Owner</TableColumn>
                    <TableColumn>Date</TableColumn>
                  </TableHeader>
                  <TableBody
                    emptyContent={
                      transfers === null
                        ? 'No objekt specified.'
                        : 'Not minted yet.'
                    }
                  >
                    {(transfers ?? []).map((transfer, index) => (
                      <TableRow key={index}>
                        <TableCell className='overflow-hidden text-ellipsis'>
                          {nicknames[transfer.to] ? (
                            <Link
                              className='inline'
                              to={`/@${nicknames[transfer.to]}`}
                            >
                              {nicknames[transfer.to]}
                            </Link>
                          ) : (
                            <Link className='inline' to={`/${transfer.to}`}>
                              {transfer.to}
                            </Link>
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(
                            timestamps[transfer.blockNumber] * 1000,
                          ).toLocaleString('en-US')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Tab>
            </Tabs>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color='danger' onPress={onClose}>
          Close
        </Button>
      </ModalFooter>
    </>
  );
}

export function filterObjekts(
  objekts: Objekt[],
  members: string[],
  seasons: string[],
  classes: string[],
  filterOption: 'all' | 'owned' | 'transferable',
  ownedObjekts: Objekt[],
) {
  objekts = objekts
    .filter(objekt => (members.length ? members.includes(objekt.member) : true))
    .filter(objekt => (seasons.length ? seasons.includes(objekt.season) : true))
    .filter(objekt => (classes.length ? classes.includes(objekt.class) : true));
  switch (filterOption) {
    case 'owned':
      objekts = objekts.filter(objekt =>
        ownedObjekts.some(
          ownedObjekt => ownedObjekt.collectionId === objekt.collectionId,
        ),
      );
      break;
    case 'transferable':
      objekts = objekts.filter(objekt =>
        ownedObjekts.some(
          ownedObjekt =>
            ownedObjekt.collectionId === objekt.collectionId &&
            ownedObjekt.transferable,
        ),
      );
      break;
    case 'all':
      break;
  }
  return objekts;
}

function ObjektsTab({
  objekts,
  artists,
}: {
  objekts: Objekt[];
  artists: Artist[];
}) {
  let currentAccount = useCurrentAccount();
  let credentials = currentAccount?.credentials;
  let ramperWalletSecrets = currentAccount?.ramperWalletSecrets;

  let ownedObjekts = usePaginatedObjekts(
    async startAfter => {
      if (!credentials) {
        return { hasNext: false, total: 0, objekts: [] };
      }
      return await cosmoClient.getOwnedObjektsByMe(startAfter);
    },
    [credentials],
  );

  let [myWantsList, setMyWantsList] = useState<string[]>([]);
  useEffect(() => {
    if (!currentAccount) return;
    let isCancelled = false;
    (async () => {
      try {
        let { lists } = await polarisClient.getUserDetails(
          currentAccount.profile.address,
        );
        if (!isCancelled) {
          setMyWantsList(
            lists.find(list => list.name === 'want')?.collectionIds || [],
          );
        }
      } catch (error) {
        console.error('Failed to fetch wants/haves lists:', error);
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [currentAccount]);

  async function onListChange() {
    if (!currentAccount) return;
    try {
      let { lists } = await polarisClient.getUserDetails(
        currentAccount.profile.address,
      );
      setMyWantsList(
        lists.find(list => list.name === 'want')?.collectionIds || [],
      );
    } catch (error) {
      console.error('Failed to reload wants/haves lists:', error);
    }
  }

  let search = useMemo(() => {
    let search = new Search('collectionId');
    search.addIndex('artists');
    search.addIndex('season');
    search.addIndex('class');
    search.addIndex('member');
    search.addIndex('collectionId');
    search.addDocuments(objekts);
    return search;
  }, [objekts]);

  let artistOptions = useMemo(
    () =>
      Array.from(
        new Set(objekts.flatMap(objekt => objekt.artists ?? ['tripleS'])),
      ),
    [objekts],
  );
  let [artistName, setArtistName] = useSearchParamState('artist', 'tripleS');
  let artist = artists.find(artist => artist.name === artistName);

  objekts = useMemo(
    () =>
      objekts
        .filter(objekt => (objekt.artists ?? ['tripleS']).includes(artistName))
        .sort((a, b) => {
          let aIndex =
            artist?.members.findIndex(member => member.name === a.member) ?? -1;
          let bIndex =
            artist?.members.findIndex(member => member.name === b.member) ?? -1;
          if (aIndex === -1) {
            return 1;
          } else if (bIndex === -1) {
            return -1;
          } else {
            return aIndex - bIndex;
          }
        }),
    [objekts, artistName, artist],
  );

  let memberOptions = useMemo(
    () =>
      Array.from(new Set(objekts.map(objekt => objekt.member))).sort((a, b) => {
        let aIndex =
          artist?.members.findIndex(member => member.name === a) ?? -1;
        let bIndex =
          artist?.members.findIndex(member => member.name === b) ?? -1;
        if (aIndex === -1) {
          return 1;
        } else if (bIndex === -1) {
          return -1;
        } else {
          return aIndex - bIndex;
        }
      }),
    [objekts],
  );
  let seasonOptions = useMemo(
    () => Array.from(new Set(objekts.map(objekt => objekt.season))),
    [objekts],
  );
  let classOptions = useMemo(
    () => Array.from(new Set(objekts.map(objekt => objekt.class))),
    [objekts],
  );

  let [members, setMembers] = useSearchParamStates('members');
  let [seasons, setSeasons] = useSearchParamStates('seasons');
  let [classes, setClasses] = useSearchParamStates('classes');
  let [query, setQuery] = useSearchParamState('q', '', { replace: true });
  let [filterOption, setFilterOption] = useState<
    'all' | 'owned' | 'transferable'
  >('all');

  let isWantsList = !!useMatch('/wants');
  let selectedList: 'all' | 'wants' = isWantsList ? 'wants' : 'all';
  let navigate = useNavigate();
  function setSelectedList(selectedList: 'all' | 'wants') {
    switch (selectedList) {
      case 'all':
        navigate('/');
        break;
      case 'wants':
        navigate('/wants');
        break;
    }
  }

  objekts = useMemo(() => {
    objekts = filterObjekts(
      objekts,
      members,
      seasons,
      classes,
      filterOption,
      ownedObjekts,
    );
    if (selectedList === 'wants') {
      objekts = objekts.filter(objekt =>
        myWantsList.includes(objekt.collectionId),
      );
    }
    return objekts;
  }, [
    objekts,
    members,
    seasons,
    classes,
    filterOption,
    ownedObjekts,
    selectedList,
    myWantsList,
  ]);

  // query changes frequently, so should be memoized separately.
  objekts = useMemo(() => {
    if (query) {
      objekts = (search.search(query) as Objekt[])
        .filter(objekt => objekts.includes(objekt))
        .sort((a, b) => {
          let aIndex =
            artist?.members.findIndex(member => member.name === a.member) ?? -1;
          let bIndex =
            artist?.members.findIndex(member => member.name === b.member) ?? -1;
          if (aIndex === -1) {
            return 1;
          } else if (bIndex === -1) {
            return -1;
          } else {
            return aIndex - bIndex;
          }
        });
    }
    return objekts;
  }, [objekts, search, query]);

  let [selectedObjekts, setSelectedObjekts_] = useState<Objekt[]>([]);

  let [error, setError] = useState('');
  let [isLoading, setIsLoading] = useState(false);

  let [recipientNickname, setRecipientNickname_] = useState('');
  function setRecipientNickname(newRecipientNickname: SetStateAction<string>) {
    setRecipientNickname_(newRecipientNickname);
    setError('');
  }

  let [txStates, setTxStates] = useState<
    { status: 'initial' | 'sending' | 'success' | 'error'; error: string }[]
  >([]);

  function setSelectedObjekts(newSelectedObjekts: SetStateAction<Objekt[]>) {
    if (isLoading) {
      return;
    }
    if (typeof newSelectedObjekts === 'function') {
      newSelectedObjekts = newSelectedObjekts(selectedObjekts);
    }
    setSelectedObjekts_(newSelectedObjekts);
    setTxStates(
      newSelectedObjekts.map(() => ({ status: 'initial', error: '' })),
    );
  }

  async function onSendButtonPress() {
    if (!ramperWalletSecrets) {
      return;
    }
    try {
      setIsLoading(true);
      setError('');
      let provider = new AlchemyProvider(
        'matic',
        'jKHL8FBDC9OR14KUb_n-J0_5KoF9hjDo',
      );
      let feeData = await provider.getFeeData();

      let { profile: recipientProfile } = await cosmoClient.get<{
        profile: {
          nickname: string;
          address: string;
          profileImageUrl: string;
        };
      }>(`/user/v1/by-nickname/${recipientNickname}`);

      let contractAddresses = {
        artms: '0x0fB69F54bA90f17578a59823E09e5a1f8F3FA200',
        tripleS: '0xA4B37bE40F7b231Ee9574c4b16b7DDb7EAcDC99B',
      };
      let contractAbi = [
        'function transferFrom(address from, address to, uint256 tokenId) public',
      ];
      let contracts = {
        artms: new Contract(contractAddresses['artms'], contractAbi, provider),
        tripleS: new Contract(
          contractAddresses['tripleS'],
          contractAbi,
          provider,
        ),
      };
      let { version, dek, encryptedKey } = ramperWalletSecrets;
      let wallet = Wallet.fromPhrase(
        await getWalletMnemonic(version, dek, encryptedKey),
        provider,
      );

      let senderAddress = wallet.address;
      let recipientAddress = recipientProfile.address;

      let nonce = await wallet.getNonce();
      let hasError = false;

      for (let i = 0; i < selectedObjekts.length; i += 8) {
        let selectedObjektsBatch = selectedObjekts.slice(i, i + 8);
        await Promise.all(
          selectedObjektsBatch.map(async (selectedObjekt, j) => {
            let index = i + j;
            try {
              let artist = (selectedObjekt.artists ?? ['tripleS'])[0] as
                | 'artms'
                | 'tripleS';
              let tokenId = selectedObjekt.tokenId;
              let contract = contracts[artist];
              let contractAddress = contractAddresses[artist];
              let txData = contract.interface.encodeFunctionData(
                'transferFrom',
                [senderAddress, recipientAddress, tokenId],
              );
              let tx = {
                nonce: nonce++,
                // gasLimit: 300000,
                gasLimit: await wallet.estimateGas({
                  to: contractAddress,
                  data: txData,
                }),
                maxFeePerGas: (feeData.maxFeePerGas! * 160n) / 100n,
                maxPriorityFeePerGas:
                  (feeData.maxPriorityFeePerGas! * 160n) / 100n,
                to: contractAddress,
                data: txData,
              };
              setTxStates(txStates => [
                ...txStates.slice(0, index),
                { status: 'sending', error: '' },
                ...txStates.slice(index + 1),
              ]);
              let txResult = await wallet.sendTransaction(tx);
              let txReceipt = await txResult.wait();
              console.log(txReceipt);
              setTxStates(txStates => [
                ...txStates.slice(0, index),
                { status: 'success', error: '' },
                ...txStates.slice(index + 1),
              ]);
            } catch (error) {
              // .catch(error =>
              hasError = true;
              console.error(error);
              console.dir(error);
              let message: string;
              if (isEthersError(error, 'CALL_EXCEPTION')) {
                message = error.reason ?? 'A blockchain error has occurred.';
                if (
                  error.reason ===
                  'ERC721: transfer caller is not owner nor approved'
                ) {
                  message = 'You have already sent this objekt.';
                }
              } else if (isEthersError(error, 'TRANSACTION_REPLACED')) {
                message =
                  'Transaction was replaced\nThis usually means you have sent other objekts in a different method.';
              } else if (error instanceof Error) {
                message = error.message;
              } else {
                message = 'An unknown error has occurred.';
              }
              setTxStates(txStates => [
                ...txStates.slice(0, index),
                { status: 'error', error: message },
                ...txStates.slice(index + 1),
              ]);
            }
          }),
        );
      }
      if (!hasError) {
        setSelectedObjekts([]);
      }
    } catch (error) {
      console.error(error);
      if (error instanceof Error) {
        setError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  }

  let [isCardOpen, setIsCardOpen] = useState(true);
  let [selectedObjektForModal, setSelectedObjektForModal] =
    useState<Objekt | null>(null);
  let {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onOpenChange: onModalOpenChange,
  } = useDisclosure();

  function onObjektPress(objekt: Objekt) {
    setSelectedObjektForModal(objekt);
    onModalOpen();
  }

  return (
    <div className='flex flex-col gap-4'>
      <ObjektFilters
        artist={artistName}
        members={members}
        seasons={seasons}
        classes={classes}
        artistOptions={artistOptions}
        memberOptions={memberOptions}
        seasonOptions={seasonOptions}
        classOptions={classOptions}
        setArtist={setArtistName}
        setMembers={setMembers}
        setSeasons={setSeasons}
        setClasses={setClasses}
        query={query}
        setQuery={setQuery}
      />
      <div className='flex flex-col w-full sm:flex-row gap-4'>
        {selectedList === 'all' ? (
          <RadioGroup
            label='Filter options'
            orientation='horizontal'
            value={filterOption}
            onValueChange={value =>
              setFilterOption(value as 'all' | 'owned' | 'transferable')
            }
            className='grow order-2 sm:order-1'
          >
            <Radio value='all'>All</Radio>
            <Radio value='owned'>Owned</Radio>
            <Radio value='transferable'>Sendable</Radio>
          </RadioGroup>
        ) : (
          <div className='flex flex-row gap-2 grow order-2 sm:order-1' />
        )}
        <Select
          label='Display List'
          selectedKeys={[selectedList]}
          onSelectionChange={selectedKeys =>
            setSelectedList(Array.from(selectedKeys as Set<'all' | 'wants'>)[0])
          }
          className='w-[auto] sm:max-w-xs order-1 sm:order-2 flex-1'
        >
          <SelectItem key='all'>All Objekts</SelectItem>
          <SelectItem key='wants'>My Wants</SelectItem>
        </Select>
      </div>
      <div className='text-medium font-medium'>
        {query && `Search results for: ${query}`}
      </div>
      <Modal size='3xl' isOpen={isModalOpen} onOpenChange={onModalOpenChange}>
        <ModalContent>
          {onClose => (
            <ObjektModal
              objekt={selectedObjektForModal!}
              onClose={onClose}
              ownedObjekts={ownedObjekts
                .filter(
                  objekt =>
                    objekt.collectionId ===
                    selectedObjektForModal!.collectionId,
                )
                .toSorted((a, b) => a.objektNo - b.objektNo)}
              selectedObjekts={selectedObjekts}
              setSelectedObjekts={setSelectedObjekts}
            />
          )}
        </ModalContent>
      </Modal>
      {objekts.length === 0 ? (
        <div className='flex flex-col gap-2 items-center'>
          <div className='text-small font-medium text-foreground-500'>
            {selectedList === 'wants'
              ? 'Mark any objekt as Want to share your Want list.'
              : 'No objekt matches your query.'}
          </div>
          {selectedList === 'wants' && (
            <img
              className='max-w-96'
              src={
                new URL('../resources/add-to-list.png', import.meta.url).href
              }
            />
          )}
        </div>
      ) : null}
      <ObjektList
        objekts={objekts}
        ownedObjekts={ownedObjekts}
        selectedObjekts={selectedObjekts}
        setSelectedObjekts={setSelectedObjekts}
        onObjektPress={onObjektPress}
        wantsList={myWantsList}
        onListChange={onListChange}
      />
      <AnimatePresence>
        {selectedObjekts.length > 0 && (
          <motion.div
            className='fixed inset-x-6 bottom-4 max-w-lg mx-auto'
            variants={TRANSITION_VARIANTS.scaleSpringOpacity}
            initial='initial'
            animate='enter'
            exit='exit'
          >
            <Card className='max-h-[60vh]'>
              <CardHeader>
                <div className='flex-1 font-bold'>
                  {selectedObjekts.length} Selected Objekt
                  {selectedObjekts.length > 1 ? 's' : ''}
                </div>
                <button
                  className='appearance-none select-none p-2 text-foreground-500 rounded-full hover:bg-default-100 active:bg-default-200 tap-highlight-transparent data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2'
                  onClick={() => setIsCardOpen(!isCardOpen)}
                >
                  {isCardOpen ? <ChevronDownIcon /> : <ChevronUpIcon />}
                </button>
              </CardHeader>
              {isCardOpen ? (
                <>
                  <Divider />
                  <ScrollShadow>
                    <CardBody>
                      {selectedObjekts.map((objekt, i) => (
                        <Fragment key={objekt.tokenId}>
                          <div className='flex h-8'>
                            <Checkbox
                              className='max-w-none flex-1'
                              isSelected={selectedObjekts.includes(objekt)}
                              onValueChange={isSelected => {
                                if (isSelected) {
                                  setSelectedObjekts([
                                    ...selectedObjekts,
                                    objekt,
                                  ]);
                                } else {
                                  setSelectedObjekts(
                                    selectedObjekts.filter(
                                      selectedObjekt =>
                                        selectedObjekt !== objekt,
                                    ),
                                  );
                                }
                              }}
                            >
                              {objekt.collectionId} #{objekt.objektNo}
                            </Checkbox>
                            {txStates[i].status === 'sending' && (
                              <Spinner size='sm' className='w-8 h-8' />
                            )}
                            {txStates[i].status === 'error' && (
                              <Popover>
                                <PopoverTrigger>
                                  <Button
                                    isIconOnly
                                    size='sm'
                                    variant='light'
                                    color='warning'
                                    aria-label='View error'
                                  >
                                    <ErrorIcon className='w-6 h-6' />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent>
                                  <p className='max-w-[20rem] text-danger break-words'>
                                    {txStates[i].error
                                      .split('\n')
                                      .map((line, index) => (
                                        <Fragment key={index}>
                                          {index !== 0 && <br />}
                                          {line}
                                        </Fragment>
                                      ))}
                                  </p>
                                </PopoverContent>
                              </Popover>
                            )}
                            {txStates[i].status === 'success' && (
                              <CheckIcon className='text-success w-8 h-8 p-1' />
                            )}
                          </div>
                        </Fragment>
                      ))}
                    </CardBody>
                  </ScrollShadow>
                  <Divider />
                  <CardBody className='shrink-0'>
                    <CosmoNicknameAutocomplete
                      nickname={recipientNickname}
                      setNickname={setRecipientNickname}
                      isInvalid={!!error}
                      errorMessage={error}
                    />
                  </CardBody>
                  <Divider />
                  <CardFooter className='shrink-0'>
                    <Button
                      color='primary'
                      onClick={onSendButtonPress}
                      isLoading={isLoading}
                    >
                      Send
                    </Button>
                  </CardFooter>
                </>
              ) : null}
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function TradesTab({ address }: { address: string }) {
  let [transfers, setTransfers] = useState<
    {
      from: string;
      to: string;
      blockNumber: number;
      objekt: Objekt;
    }[]
  >([]);
  let [nicknames, setNicknames] = useState<{
    [address: string]: string;
  }>({});
  let [timestamps, setTimestamps] = useState<{
    [blockNumber: number]: number;
  }>({});

  useEffect(() => {
    let isCancelled = false;
    (async () => {
      let { transfers } = await polarisClient.getUserDetails(address);
      let addresses = new Set<string>();
      let blockNumbers = new Set<number>();
      for (let transfer of transfers) {
        if (transfer.from !== '0x0000000000000000000000000000000000000000') {
          addresses.add(transfer.from);
        }
        addresses.add(transfer.to);
        blockNumbers.add(transfer.blockNumber);
      }
      await Promise.all([
        getNicknames(Array.from(addresses)).then(nicknames => {
          if (!isCancelled) {
            setTransfers(transfers);
            setNicknames(nicknames);
          }
        }),
        getTimestamps(Array.from(blockNumbers)).then(timestamps => {
          if (!isCancelled) {
            setTransfers(transfers);
            setTimestamps(timestamps);
          }
        }),
      ]);
      // if (!isCancelled) {
      //   setTransfers(transfers);
      //   setNicknames(nicknames);
      //   setTimestamps(timestamps);
      // }
    })();
    return () => void (isCancelled = true);
  }, [address]);

  let parentRef = useRef<HTMLDivElement>(null);
  let parentOffsetRef = useRef(0);
  useLayoutEffect(() => {
    parentOffsetRef.current = parentRef.current?.offsetTop ?? 0;
  }, []);

  let virtualizer = useWindowVirtualizer({
    count: transfers.length,
    estimateSize: () => 40,
    scrollMargin: parentOffsetRef.current,
  });
  let virtualItems = virtualizer.getVirtualItems();

  return (
    <div ref={parentRef}>
      <div style={{ height: virtualizer.getTotalSize() }} className='relative'>
        <div
          className='absolute top-0 inset-x-0'
          style={{
            transform: `translateY(${
              (virtualItems[0]?.start ?? 0) - virtualizer.options.scrollMargin
            }px)`,
          }}
        >
          {virtualItems.map(item => {
            let transfer = transfers[item.index];
            return (
              <div
                key={item.index}
                data-index={item.index}
                ref={virtualizer.measureElement}
              >
                <div className='flex flex-col gap-1 md:flex-row md:gap-4 mx-auto py-2 items-center justify-center text-center'>
                  <div className='md:basis-48'>
                    {timestamps[transfer.blockNumber] ? (
                      new Date(
                        timestamps[transfer.blockNumber] * 1000,
                      ).toLocaleString('en-US')
                    ) : (
                      <Spinner size='sm' />
                    )}
                  </div>
                  <Divider
                    orientation='vertical'
                    className='hidden md:block h-auto self-stretch'
                  />
                  <div className='md:basis-64'>
                    {transfer.objekt.collectionId} #{transfer.objekt.objektNo}
                  </div>
                  <Divider
                    orientation='vertical'
                    className='hidden md:block h-auto self-stretch'
                  />
                  <div className='flex gap-2 md:basis-48 items-center justify-center'>
                    {transfer.to === address ? (
                      <Chip size='sm' color='primary'>
                        From
                      </Chip>
                    ) : (
                      <Chip size='sm' color='secondary'>
                        To
                      </Chip>
                    )}
                    {transfer.to === address ? (
                      transfer.from ===
                      '0x0000000000000000000000000000000000000000' ? (
                        'COSMO'
                      ) : nicknames[transfer.from] ? (
                        <Link
                          className='inline'
                          to={`/@${nicknames[transfer.from]}`}
                        >
                          {nicknames[transfer.from]}
                        </Link>
                      ) : (
                        <Link className='inline' to={`/${transfer.from}`}>
                          {transfer.from}
                        </Link>
                      )
                    ) : nicknames[transfer.to] ? (
                      <Link
                        className='inline'
                        to={`/@${nicknames[transfer.to]}`}
                      >
                        {nicknames[transfer.to]}
                      </Link>
                    ) : (
                      <Link className='inline' to={`/${transfer.to}`}>
                        {transfer.to}
                      </Link>
                    )}
                  </div>
                </div>
                <Divider className='md:max-w-[46rem] mx-auto' />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export async function getNicknames(
  addresses: string[],
): Promise<{ [address: string]: string }> {
  if (addresses.length == 0) {
    return {};
  }
  let promises: Promise<[string, string][]>[] = [];
  for (let i = 0; i < addresses.length; i += 150) {
    let addressesBatch = addresses.slice(i, i + 150);
    promises.push(
      (async () => {
        let response = await fetch(
          `https://cache.nova.gd/user/v1/by-address/${addressesBatch.join(
            ',',
          )}`,
        );
        let json = (await response.json()) as {
          nickname: string;
          address: string;
          profileImageUrl: string;
        }[];
        return json.map(({ address, nickname }) => [address, nickname]);
      })(),
    );
  }
  let entries = (await Promise.all(promises)).flat();
  return Object.fromEntries(entries);
}

let provider = new AlchemyProvider('matic', 'jKHL8FBDC9OR14KUb_n-J0_5KoF9hjDo');

export async function getTimestamps(
  blockNumbers: number[],
): Promise<{ [blockNumber: number]: number }> {
  let entries = (
    await Promise.all(
      Array.from(blockNumbers).map(async blockNumber => [
        blockNumber,
        (await provider.getBlock(blockNumber))?.timestamp,
      ]),
    )
  ).filter(([, timestamp]) => !!timestamp);
  return Object.fromEntries(entries);
}

export default function Objekts() {
  let { objekts, artists } = useLoaderData() as {
    objekts: Objekt[];
    artists: Artist[];
  };
  let currentAccount = useCurrentAccount();
  let isTradeTab = !!useMatch('/trades');
  let navigate = useNavigate();

  return (
    <div className='max-w-5xl mx-auto px-6 py-2 flex flex-col gap-4'>
      <Tabs
        disabledKeys={currentAccount ? [] : ['trades']}
        selectedKey={isTradeTab ? 'trades' : 'objekts'}
        onSelectionChange={key => {
          if (key === 'trades') {
            navigate('/trades');
          } else {
            navigate('/');
          }
        }}
      >
        <Tab
          key='objekts'
          title={currentAccount ? 'My Objekts' : 'All Objekts'}
        >
          <ObjektsTab objekts={objekts} artists={artists} />
        </Tab>
        <Tab key='trades' title='My Trades'>
          {currentAccount && (
            <TradesTab address={currentAccount.profile.address} />
          )}
        </Tab>
      </Tabs>
    </div>
  );
}
